/* eslint-disable @next/next/no-img-element */
import Link from 'next/link'
import * as React from 'react'

import { SignInForm } from '@/components/forms/auth/sign-in-form'

import { NON_FSDS_DEFAULT_LOGO_URL } from '@/configs/non-fsds-config'
import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'

export interface NonFsdsSignInSectionProps {
  heroTitle: string
  heroDescription: string
  formTitle: string
  formSubtitle: string
  logoUrl: string
  signUpText: string
  brandHref?: string
  signUpUrl?: string
  gradientFrom: string
  gradientTo: string
}

export default function NonFsdsSignInSection({
  heroTitle = 'Unlock Your\nPotential',
  heroDescription = 'Sign in to explore your personalized learning journey, track your progress, and connect with peers.',
  formTitle = 'Welcome Back',
  formSubtitle = 'Please enter your details to sign in.',
  logoUrl = NON_FSDS_DEFAULT_LOGO_URL,
  signUpText = 'Sign up',
  brandHref = TEMPLATE_DEFAULT_URLS.home,
  signUpUrl = TEMPLATE_DEFAULT_URLS.signUp,
  gradientFrom = '#1d4ed8',
  gradientTo = '#1e3a8a',
}: NonFsdsSignInSectionProps) {
  const heroLines = heroTitle.split('\n')

  return (
    <div className="min-h-svh flex items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row w-full max-w-5xl bg-white rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] overflow-hidden">
        <div
          className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden text-white"
          style={{ background: `linear-gradient(to bottom right, ${gradientFrom}, ${gradientTo})` }}
        >
          <div className="absolute top-0 left-0 w-full h-full opacity-30 bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-blue-400 via-transparent to-transparent" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative z-10">
            <Link href={brandHref} className="inline-block transition-transform hover:scale-105 active:scale-95 duration-200">
              <img src={logoUrl} alt="Logo" className="h-10 w-auto" />
            </Link>
          </div>

          <div className="relative z-10 mt-auto">
            <h2 className="text-4xl font-bold tracking-tight mb-4 text-white leading-tight">
              {heroLines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < heroLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </h2>
            <p className="text-blue-100/90 text-lg max-w-[22rem] leading-relaxed">{heroDescription}</p>
          </div>
        </div>

        <div className="w-full lg:w-1/2 p-8 sm:p-12 lg:p-16 flex flex-col justify-center bg-white relative z-10">
          <div className="mx-auto w-full max-w-sm">
            <div className="lg:hidden mb-10 flex justify-center">
              <Link href={brandHref} className="inline-block">
                <img src={logoUrl} alt="Logo" className="h-10 w-auto" />
              </Link>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-2">{formTitle}</h3>
            <p className="text-slate-500 mb-8 text-sm">{formSubtitle}</p>

            <SignInForm
              disabledSignIn={false}
              buttonClassName="w-full bg-blue-600 text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:bg-blue-700 hover:shadow-blue-600/40 focus-visible:ring-blue-600 rounded-xl"
            />

            <div className="mt-8 text-center text-sm text-slate-500">
              Don&apos;t have an account?{' '}
              <Link
                href={signUpUrl}
                className="font-semibold text-blue-600 transition-colors hover:text-blue-700 hover:underline underline-offset-4"
              >
                {signUpText}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
